/**
 * Clarifii Reliance Letter Helpers
 *
 * Pure functions for investor-name matching, recipient deduplication,
 * and email composition. No React or side-effect dependencies.
 */

// ── Fixed recipients ────────────────────────────────────────────────────────

export const CLARIFII_FIXED_TO = [
  'reliance_support@clarifii.com',
  'Adam.Peat@clarifii.com'
] as const;

export const CLARIFII_CC = [
  'jeremy@lendwisemtg.com'
] as const;

// ── Investor name normalization ─────────────────────────────────────────────

/**
 * Normalize investor name for matching: lowercase, strip common suffixes,
 * remove punctuation, collapse whitespace.
 *
 * "Vista Point Mortgage"       → "vista point"
 * "VISTA POINT MORTGAGE"       → "vista point"
 * "Vista Point Mortgage, LLC"  → "vista point"
 */
export function normalizeInvestorName(name: string): string {
  return name
    .toLowerCase()
    .replace(/,/g, '')
    .replace(/\b(llc|inc|corp|corporation|mortgage|lending|company|co)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// ── Investor matching ───────────────────────────────────────────────────────

/**
 * Map structure: normalized investor name → investor-specific TO recipients.
 * Built from investors table rows where clarifi_reliance_required = true.
 */
export type ClarifiiInvestorConfigMap = Map<string, string[]>;

/**
 * Match a loan's investor_name against the Clarifii config map.
 * Returns the investor-specific recipients if matched, or null.
 * Uses exact normalized equality to prevent prefix collisions.
 */
export function matchesClarifiiInvestor(
  loanInvestorName: string | null | undefined,
  configMap: ClarifiiInvestorConfigMap
): string[] | null {
  if (!loanInvestorName) {
return null;
}
  const normalized = normalizeInvestorName(loanInvestorName);
  const recipients = configMap.get(normalized);
  return recipients ?? null;
}

// ── Recipient deduplication ─────────────────────────────────────────────────

/**
 * Combine fixed Clarifii addresses with investor-specific addresses,
 * removing duplicates: both against fixed addresses and within the
 * investor-entered list itself (case-insensitive).
 */
export function dedupeRecipients(fixed: readonly string[], investorSpecific: string[]): string[] {
  const seen = new Set(fixed.map(e => e.toLowerCase().trim()));
  const deduped: string[] = [];
  for (const email of investorSpecific) {
    const key = email.toLowerCase().trim();
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(email);
    }
  }
  return [...fixed, ...deduped];
}

// ── Borrower name formatting ───────────────────────────────────────────────

/**
 * Build a borrower display name from first/last. Returns empty string
 * if both are blank/null. Trims and collapses whitespace.
 *
 * Must stay in sync with server-side formatBorrowerName in clarifii-reliance.ts.
 */
export function formatBorrowerName(
  firstName?: string | null,
  lastName?: string | null
): string {
  return [firstName, lastName]
    .map(n => (n || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(' ');
}

// ── Email composition ───────────────────────────────────────────────────────

export interface ClarifiiRelianceEmail {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
}

/**
 * Compose a Clarifii reliance letter request email.
 * Caller must validate that all loan fields are present before calling.
 * Email template is Vista-specific by policy (v1).
 *
 * When borrower names are provided and non-empty, the borrower name appears
 * in the subject and body. When blank/omitted, the borrower segment is
 * omitted entirely — the send is never blocked by a missing name.
 *
 * Must produce the same subject/body as server-side composeClarifiiEmail.
 */
export function composeClarifiiRelianceEmail(
  loan: {
    trans_details_loan_number: string;
    rate_lock_investor_commitment: string;
    borrower_first_name?: string | null;
    borrower_last_name?: string | null;
  },
  investorRecipients: string[]
): ClarifiiRelianceEmail {
  const borrowerName = formatBorrowerName(loan.borrower_first_name, loan.borrower_last_name);
  const hasBorrower = !!borrowerName;
  const subject = hasBorrower
    ? `Reliance letter for ${loan.trans_details_loan_number} - ${borrowerName} - Vista`
    : `Reliance letter for ${loan.trans_details_loan_number} - Vista`;
  const bodyLines = [
    'Hi Team,',
    '',
    `Can you create a reliance letter for loan number ${loan.trans_details_loan_number}, Vista's loan number is ${loan.rate_lock_investor_commitment}.`
  ];
  if (hasBorrower) {
    bodyLines.push(`Borrower name: ${borrowerName}.`);
  }
  bodyLines.push('', 'Thank you');

  return {
    to: dedupeRecipients(CLARIFII_FIXED_TO, investorRecipients),
    cc: [...CLARIFII_CC],
    subject,
    body: bodyLines.join('\n')
  };
}
